import React, { useState, useEffect, useRef } from 'react';

interface Props {
  open: boolean;
  title: string;
  message: string;
  inputPlaceholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1100, padding: 24,
  },
  modal: {
    background: 'white', borderRadius: 12, maxWidth: 440, width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
  },
  body: { padding: '24px 24px 16px' },
  title: { fontSize: 18, fontWeight: 700, color: '#1a365d', margin: '0 0 8px' },
  message: { fontSize: 14, color: '#4a5568', margin: '0 0 14px', lineHeight: 1.5 },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #cbd5e0',
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const, outline: 'none',
  },
  actions: {
    display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '12px 24px', background: '#f7fafc', borderTop: '1px solid #e2e8f0',
  },
  cancelBtn: {
    background: 'white', color: '#333', border: '1px solid #cbd5e0',
    padding: '8px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
  },
  confirmBtn: {
    background: '#0078d4', color: 'white', border: 'none', padding: '8px 20px',
    borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 600,
  },
  confirmBtnDisabled: {
    background: '#a0aec0', color: 'white', border: 'none', padding: '8px 20px',
    borderRadius: 6, fontSize: 14, cursor: 'not-allowed', fontWeight: 600,
  },
};

export default function InputModal({ open, title, message, inputPlaceholder, confirmLabel, cancelLabel, onConfirm, onCancel }: Props) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const canConfirm = value.trim().length > 0;

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.body}>
          <h3 style={styles.title}>{title}</h3>
          <p style={styles.message}>{message}</p>
          <input
            ref={inputRef}
            style={styles.input}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={inputPlaceholder || ''}
            onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) onConfirm(value.trim()); }}
          />
        </div>
        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onCancel}>{cancelLabel || 'Cancel'}</button>
          <button
            style={canConfirm ? styles.confirmBtn : styles.confirmBtnDisabled}
            onClick={() => canConfirm && onConfirm(value.trim())}
            disabled={!canConfirm}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
